/**
 * Refine adapter (#540, lifecycle-doc-model R6).
 *
 * The single seam through which rough input becomes a structured
 * lifecycle document. Today it wraps the SystemDox AI spec generator;
 * when the Noteble capture package lands (product-noteble#1265), it
 * supplies its own adapter implementing this interface — nothing else
 * in the app changes. Callers depend on the interface, never on aiApi
 * directly.
 */

import { aiApi, type GeneratedSpec } from '@/services/aiApi';
import type { DocType } from '@/services/specsApi';

export interface RefineRequest {
  /** Raw captured input — notes, transcript, or a rough description. */
  input: string;
  /** Which pillar the output is destined for. */
  docType: Extract<DocType, 'requirement' | 'design'>;
}

/** A structured document ready to persist as a spec. */
export interface RefinedDocument {
  name: string;
  proposal: string;
  requirements: { name: string; content: string }[];
  tasks: string;
}

export interface RefineAdapter {
  refine(request: RefineRequest): Promise<RefinedDocument>;
}

/** Default adapter: SystemDox's server-side AI spec generation. */
export const systemdoxRefineAdapter: RefineAdapter = {
  async refine({ input }: RefineRequest): Promise<RefinedDocument> {
    const spec: GeneratedSpec = await aiApi.generateSpec(input);
    return {
      name: spec.name,
      proposal: spec.proposal,
      requirements: spec.requirements,
      tasks: spec.tasks,
    };
  },
};

export interface ValidationIssue {
  severity: 'error' | 'warning';
  message: string;
}

const RFC2119 = /\b(MUST(?:\s+NOT)?|SHALL(?:\s+NOT)?|SHOULD(?:\s+NOT)?|MAY)\b/;
const GWT = /\bgiven\b[\s\S]*\bwhen\b[\s\S]*\bthen\b/i;

/**
 * Validate a refined requirement against the shared-specs convention
 * (R6) before it is saved. Hard structural problems are errors and
 * should block the save; convention gaps are warnings the author can
 * fix in the editor. Design docs skip the requirement-shape checks.
 */
export function validateRefinedDocument(
  doc: RefinedDocument,
  docType: RefineRequest['docType'],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!doc.name.trim()) {
    issues.push({ severity: 'error', message: 'The document has no name.' });
  }
  if (!doc.proposal.trim()) {
    issues.push({ severity: 'error', message: 'The proposal is empty.' });
  }

  if (docType === 'requirement') {
    if (doc.requirements.length === 0) {
      issues.push({ severity: 'error', message: 'No requirements were produced.' });
    }
    const missingKeyword = doc.requirements.filter((r) => !RFC2119.test(r.content));
    if (missingKeyword.length > 0) {
      issues.push({
        severity: 'warning',
        message: `${missingKeyword.length} requirement(s) use no RFC 2119 keyword (MUST/SHOULD/MAY).`,
      });
    }
    const missingScenario = doc.requirements.filter((r) => !GWT.test(r.content));
    if (missingScenario.length > 0) {
      issues.push({
        severity: 'warning',
        message: `${missingScenario.length} requirement(s) have no Given/When/Then scenario.`,
      });
    }
  }

  return issues;
}

export const hasBlockingIssues = (issues: ValidationIssue[]) =>
  issues.some((i) => i.severity === 'error');
